export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

const APP_BASE = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
const HOUSE_CODE_KEY = 'ttm-house-code';
const OPEN_PRIVATE_KEY = 'ttm-open-private';

export interface HouseInfo {
  id: string;
  kind: 'public' | 'private';
  code: string | null;
  hostUserId: string | null;
  canHost: boolean;
}

function readStoredHouseCode(): string | null {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('room');
    if (fromUrl && /^[A-Za-z0-9]{4}$/.test(fromUrl.trim())) {
      return fromUrl.trim().toUpperCase();
    }
    const stored = window.localStorage.getItem(HOUSE_CODE_KEY);
    if (stored && /^[A-Z0-9]{4}$/.test(stored)) return stored;
  } catch {
    // ignore
  }
  return null;
}

let currentHouseCode: string | null = readStoredHouseCode();

export function getHouseCode(): string | null {
  return currentHouseCode;
}

export function setHouseCode(code: string | null): void {
  currentHouseCode = code ? code.trim().toUpperCase() : null;
  try {
    if (currentHouseCode) window.localStorage.setItem(HOUSE_CODE_KEY, currentHouseCode);
    else window.localStorage.removeItem(HOUSE_CODE_KEY);
    const url = new URL(window.location.href);
    if (currentHouseCode) url.searchParams.set('room', currentHouseCode);
    else url.searchParams.delete('room');
    window.history.replaceState({}, '', url.pathname + url.search);
  } catch {
    // ignore
  }
}

export function queuePrivateHouse(): void {
  try {
    window.sessionStorage.setItem(OPEN_PRIVATE_KEY, '1');
  } catch {
    // ignore
  }
}

export function takeQueuedPrivateHouse(): boolean {
  try {
    const queued = window.sessionStorage.getItem(OPEN_PRIVATE_KEY) === '1';
    window.sessionStorage.removeItem(OPEN_PRIVATE_KEY);
    return queued;
  } catch {
    return false;
  }
}

function withBase(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return `${APP_BASE}${url.startsWith('/') ? url : `/${url}`}`;
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers || {});
  if (init?.body != null && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (currentHouseCode) headers.set('X-Theater-Room', currentHouseCode);

  const res = await fetch(withBase(url), {
    ...init,
    credentials: 'include',
    headers,
  });

  if (!res.ok) {
    let message = 'Request failed';
    try {
      const data = (await res.json()) as { error?: string; message?: string };
      message = data.error || data.message || message;
    } catch {
      // ignore
    }
    throw new ApiError(message, res.status);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export interface User {
  id: string;
  username: string;
  role: 'ADMIN' | 'USER' | 'GUEST';
  display_name: string | null;
  character_id?: string | null;
}

export interface TheaterPreferences {
  theater_characters: boolean;
  thrown_effects: boolean;
  speech_bubbles: boolean;
  reactions: boolean;
}

export interface TheaterSettings {
  effectsEnabled: boolean;
  throwablesEnabled: boolean;
  chatEnabled: boolean;
  rareEventsEnabled: boolean;
  rareEventCooldownSec: number;
  nuclearTomatoProbability: number;
  adminChatHistoryLimit: number;
}

export interface AdPoster {
  name: string;
  url: string;
}

export interface Movie {
  id: string;
  title: string;
  year: number | null;
  duration_seconds: number | null;
  poster_url: string | null;
  description: string | null;
  youtube_video_id: string;
  youtube_url: string;
  source: 'youtube';
}

export interface PublicMovieListing {
  title: string;
  year: number | null;
  genre: string;
  blurb: string;
  youtube_video_id: string;
  youtube_url: string;
  poster_url: string;
}

export interface QueueItem {
  id: string;
  position: number;
  created_at: string;
  requested_by: string | null;
  requested_by_username: string | null;
  movie: Movie;
  flag_count?: number;
  flagged_by?: string[];
}

export interface PlayedItem {
  id: string;
  played_at: string;
  requested_by: string | null;
  requested_by_username: string | null;
  movie: Movie;
  flag_count?: number;
  flagged_by?: string[];
}

export type PlaybackPhase =
  | 'idle'
  | 'preparing'
  | 'countdown'
  | 'playing'
  | 'paused'
  | 'seeking'
  | 'failed'
  | 'break';

export interface AdDeckState {
  items: AdPoster[];
  started_at_ms: number;
  interval_ms: number;
  mode: 'idle' | 'break';
}

export type ReadyStatus = 'LOADING' | 'CUED' | 'READY' | 'FAILED' | 'BUFFERING';

export interface ReadyViewer {
  userId: string;
  username: string;
  displayName: string | null;
  status: ReadyStatus;
}

export interface ReadinessSnapshot {
  session_id: string;
  required: number;
  ready: number;
  failed: number;
  viewers: ReadyViewer[];
}

export interface PlaybackState {
  movie: Movie | null;
  phase: PlaybackPhase;
  is_playing: boolean;
  position_seconds: number;
  duration_seconds: number | null;
  playback_rate: number;
  started_at: string | null;
  last_updated_at: string;
  server_time: string;
  server_time_ms: number;
  delivery: {
    mode: 'youtube' | 'none';
  };
  queue_item_id: string | null;
  session_id: string | null;
  play_at: string | null;
  play_at_ms: number | null;
  prepare_deadline: string | null;
  prepare_deadline_ms: number | null;
  youtube_video_id: string | null;
  readiness: ReadinessSnapshot | null;
  error: string | null;
  flag_count?: number;
  flagged_by?: string[];
  ads?: AdDeckState | null;
  next_movie?: Movie | null;
  house?: HouseInfo;
}

export const api = {
  me: () => request<{ user: User }>('/api/auth/me'),
  login: (username: string, password: string) =>
    request<{ user: User }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  authConfig: () =>
    request<{
      appName: string;
      zemewOrigin: string;
      zemewSsoUrl: string;
      ssoReady: boolean;
    }>('/api/auth/config'),
  previewZemew: (ticket: string) =>
    request<{ username: string }>(`/api/auth/zemew/preview?ticket=${encodeURIComponent(ticket)}`),
  joinZemew: (ticket: string, characterId: string) =>
    request<{ user: User }>('/api/auth/zemew', {
      method: 'POST',
      body: JSON.stringify({ ticket, characterId }),
    }),
  logout: () => request<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),
  movies: (params?: { q?: string; shuffle?: boolean; limit?: number }) => {
    const qs = new URLSearchParams();
    if (params?.q) qs.set('q', params.q);
    qs.set('shuffle', params?.shuffle === false ? '0' : '1');
    if (params?.limit) qs.set('limit', String(params.limit));
    return request<{ movies: PublicMovieListing[]; total: number }>(`/api/movies?${qs.toString()}`);
  },
  movie: (id: string) => request<{ movie: Movie }>(`/api/movies/${id}`),
  queue: () => request<{ items: QueueItem[] }>('/api/queue'),
  addToQueue: (youtubeUrl: string) =>
    request<{ item: QueueItem; started?: boolean }>('/api/queue', {
      method: 'POST',
      body: JSON.stringify({ youtubeUrl }),
    }),
  removeQueue: (id: string) =>
    request<{ ok: boolean }>(`/api/queue/${id}`, { method: 'DELETE' }),
  clearQueue: () => request<{ ok: boolean }>('/api/queue/clear', { method: 'POST' }),
  history: () => request<{ items: PlayedItem[] }>('/api/queue/history'),
  removePlayed: (id: string) =>
    request<{ ok: boolean }>(`/api/queue/history/${id}`, { method: 'DELETE' }),
  flagQueueItem: (id: string, flagged: boolean) =>
    request<{ ok: boolean; flag_count: number; flagged_by: string[] }>(`/api/queue/${id}/flag`, {
      method: 'POST',
      body: JSON.stringify({ flagged }),
    }),
  ads: () => request<{ items: AdPoster[]; interval_ms: number }>('/api/ads'),
  house: () => request<{ house: HouseInfo }>('/api/house'),
  createPrivateHouse: () =>
    request<{ house: HouseInfo }>('/api/house/private', { method: 'POST' }),
  joinHouse: (code: string) =>
    request<{ house: HouseInfo }>('/api/house/join', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  playback: () => request<PlaybackState>('/api/playback'),
  play: (movieId: string) =>
    request<PlaybackState>('/api/playback/play', {
      method: 'POST',
      body: JSON.stringify({ movieId }),
    }),
  pause: () => request<PlaybackState>('/api/playback/pause', { method: 'POST' }),
  resume: () => request<PlaybackState>('/api/playback/resume', { method: 'POST' }),
  seek: (positionSeconds: number) =>
    request<PlaybackState>('/api/playback/seek', {
      method: 'POST',
      body: JSON.stringify({ positionSeconds }),
    }),
  restart: () => request<PlaybackState>('/api/playback/restart', { method: 'POST' }),
  stop: () => request<PlaybackState>('/api/playback/stop', { method: 'POST' }),
  skip: () => request<PlaybackState>('/api/playback/skip', { method: 'POST' }),
  startAnyway: () => request<PlaybackState>('/api/playback/start-anyway', { method: 'POST' }),
  ended: (
    movieId?: string,
    extra?: { position?: number; playerDuration?: number },
  ) =>
    request<PlaybackState>('/api/playback/ended', {
      method: 'POST',
      body: JSON.stringify({
        ...(movieId ? { movieId } : {}),
        ...(extra?.position != null ? { position: extra.position } : {}),
        ...(extra?.playerDuration != null ? { playerDuration: extra.playerDuration } : {}),
      }),
    }),
  users: () =>
    request<{
      users: Array<User & { is_disabled: boolean; created_at: string; last_login_at: string | null }>;
    }>('/api/admin/users'),
  createUser: (body: {
    username: string;
    password: string;
    role: 'ADMIN' | 'USER';
    displayName?: string;
  }) =>
    request<{ user: User }>('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  disableUser: (id: string, disabled: boolean) =>
    request<{ ok: boolean }>(`/api/admin/users/${id}/disable`, {
      method: 'POST',
      body: JSON.stringify({ disabled }),
    }),
  resetPassword: (id: string, password: string) =>
    request<{ ok: boolean }>(`/api/admin/users/${id}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),
  viewers: () =>
    request<{
      viewers: Array<{
        id: string;
        username: string;
        display_name: string | null;
        role: string;
      }>;
    }>('/api/admin/viewers'),

  theaterMe: () =>
    request<{ character: string; preferences: TheaterPreferences }>('/api/theater/me'),
  theaterCatalog: () =>
    request<{
      characters: Array<{ id: string; name: string }>;
      throwables: Array<{ id: string; name: string; emoji: string; rarity: string; cooldownMs: number }>;
    }>('/api/theater/catalog'),
  setCharacter: (characterId: string) =>
    request<{ character: string }>('/api/theater/character', {
      method: 'PUT',
      body: JSON.stringify({ characterId }),
    }),
  setTheaterPreferences: (preferences: Partial<TheaterPreferences>) =>
    request<{ preferences: TheaterPreferences }>('/api/theater/preferences', {
      method: 'PUT',
      body: JSON.stringify(preferences),
    }),
  theaterSettings: () => request<TheaterSettings>('/api/theater/settings'),

  adminTheaterSettings: () =>
    request<{
      settings: TheaterSettings;
      moderation: TheaterSettings & {
        mutedUsers: string[];
        throwDisabledUsers: string[];
        removedUsers: string[];
      };
      chat: { stored: number; limit: number };
    }>('/api/admin/theater/settings'),
  updateAdminTheaterSettings: (body: Partial<TheaterSettings>) =>
    request<{ settings: TheaterSettings }>('/api/admin/theater/settings', {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  testNuclearTomato: () =>
    request<{ ok: boolean }>('/api/admin/theater/test-nuclear-tomato', { method: 'POST' }),
  clearTheaterEffects: () =>
    request<{ ok: boolean }>('/api/admin/theater/clear-effects', { method: 'POST' }),
  muteTheaterUser: (userId: string, muted: boolean) =>
    request<{ ok: boolean }>('/api/admin/theater/mute', {
      method: 'POST',
      body: JSON.stringify({ userId, muted }),
    }),
  disableTheaterThrows: (userId: string, disabled: boolean) =>
    request<{ ok: boolean }>('/api/admin/theater/throw-disable', {
      method: 'POST',
      body: JSON.stringify({ userId, disabled }),
    }),
  removeFromTheater: (userId: string) =>
    request<{ ok: boolean }>('/api/admin/theater/remove', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),
  allowBackToTheater: (userId: string) =>
    request<{ ok: boolean }>('/api/admin/theater/allow', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),
  adminChatHistory: (params: Record<string, string | number | undefined>) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v != null && v !== '') qs.set(k, String(v));
    }
    return request<{
      messages: Array<{
        id: string;
        user_id: string | null;
        username_snapshot: string;
        message: string;
        created_at: string;
        movie_id: string | null;
        movie_title: string | null;
      }>;
      total: number;
    }>(`/api/admin/theater/chat?${qs.toString()}`);
  },
  deleteAdminChatMessage: (id: string) =>
    request<{ ok: boolean }>(`/api/admin/theater/chat/${id}`, { method: 'DELETE' }),
  clearAdminChatHistory: () =>
    request<{ ok: boolean; deleted: number }>('/api/admin/theater/chat/clear', {
      method: 'POST',
    }),
};

export function formatDuration(totalSeconds: number | null | undefined): string {
  if (totalSeconds == null || Number.isNaN(totalSeconds) || totalSeconds < 0) return '0:00';
  const s = Math.floor(totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}
